import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { ISurveyTemplateCard, TemplateStatus } from '../../../../extViewmodels/sdk/models/SurveyTemplate';
import { getDefaultDateStringValue, getIndustryName } from '../../../../models/UiUtils';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../web/components-v2/Dropdown';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IExtendedSurveyTemplateCard extends ISurveyTemplateCard {
	industries?: string[]; // List of industry names this template belongs to
}

interface ISurveyTemplatesTableProps {
	templates: IExtendedSurveyTemplateCard[];
	isLoading?: boolean;
	mutatingTemplateId?: string | null;
	nameColumnLabel?: string;
	industriesText?: string; // Deprecated: Use template.industries instead
	onTemplateClicked?: (template: ISurveyTemplateCard) => void;
	onEditTemplate?: (template: ISurveyTemplateCard) => void;
	onDuplicateTemplate?: (template: ISurveyTemplateCard) => void;
	onPublishTemplate?: (template: ISurveyTemplateCard) => void;
	onDeleteTemplate?: (template: ISurveyTemplateCard) => void;
}

export function SurveyTemplatesTable({
	templates,
	isLoading,
	mutatingTemplateId,
	nameColumnLabel = 'Survey Template Name',
	industriesText,
	onTemplateClicked,
	onEditTemplate,
	onDuplicateTemplate,
	onPublishTemplate,
	onDeleteTemplate,
}: ISurveyTemplatesTableProps) {
	return (
		<div className={css(styleSheet.container)}>
			{/* Table Header */}
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.gripperCol)}>{/* Empty gripper column */}</div>
				<div className={css(styleSheet.nameCol)}>{nameColumnLabel}</div>
				<div className={css(styleSheet.industriesCol)}>Industries</div>
				<div className={css(styleSheet.creationDateCol)}>Created</div>
				<div className={css(styleSheet.actionsCol)}>Actions</div>
			</div>

			{/* Table Body */}
			<div>
				{templates.length === 0 && !isLoading ? (
					<div className={css(styleSheet.emptyState)}>No templates found</div>
				) : (
					templates.map(template => {
						// Format industries: Use template.industries if available, otherwise fall back to industriesText
						const industriesDisplay = template.industries?.length
							? template.industries.map(ind => getIndustryName(ind as Api.Industry)).join(', ')
							: industriesText || '';

						return (
							<div key={template.id} className={css(styleSheet.row)}>
								<div className={css(styleSheet.gripperCol)}>{/* Empty gripper column for alignment */}</div>

								<div className={css(baseStyleSheet.truncateText, styleSheet.nameCol)} title={template.name}>
									<button className={css(styleSheet.nameButton)} onClick={() => onTemplateClicked?.(template)}>
										<span className={css(baseStyleSheet.truncateText)}>{template.name}</span>
										{mutatingTemplateId === template.id && (
											<LoadingSpinner className={css(styleSheet.inlineSpinner)} type='tiny' />
										)}
									</button>
								</div>

								<div className={css(baseStyleSheet.truncateText, styleSheet.industriesCol)} title={industriesDisplay}>
									{industriesDisplay}
								</div>

								<div className={css(styleSheet.creationDateCol)}>
									{getDefaultDateStringValue(template.creationDate)}
								</div>

								<div className={css(styleSheet.actionsCol)}>
									<Dropdown>
										<DropdownEllipsisTrigger styles={[styleSheet.actionsButton]} />
										<DropdownContent>
											{/* Draft actions */}
											{template.status === TemplateStatus.Draft && (
												<>
													{onEditTemplate && <DropdownItem onClick={() => onEditTemplate(template)}>Edit</DropdownItem>}
													{onPublishTemplate && (
														<DropdownItem onClick={() => onPublishTemplate(template)}>Publish</DropdownItem>
													)}
													{onDeleteTemplate && (
														<DropdownItem onClick={() => onDeleteTemplate(template)}>Archive</DropdownItem>
													)}
												</>
											)}

											{/* Published actions */}
											{template.status === TemplateStatus.Published && (
												<>
													{onEditTemplate && <DropdownItem onClick={() => onEditTemplate(template)}>Edit</DropdownItem>}
													{onDuplicateTemplate && (
														<DropdownItem onClick={() => onDuplicateTemplate(template)}>Duplicate</DropdownItem>
													)}
													{onDeleteTemplate && (
														<DropdownItem onClick={() => onDeleteTemplate(template)}>Archive</DropdownItem>
													)}
												</>
											)}

											{/* Archived actions */}
											{template.status === TemplateStatus.Archived && (
												<>
													{onPublishTemplate && (
														<DropdownItem onClick={() => onPublishTemplate(template)}>Publish</DropdownItem>
													)}
												</>
											)}
										</DropdownContent>
									</Dropdown>
								</div>
							</div>
						);
					})
				)}
			</div>

			{/* Loading Spinner */}
			{isLoading && <LoadingSpinner className={css(styleSheet.loadingSpinner)} type='large' />}
		</div>
	);
}
